import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const WorkOrdersPanel = ({ workOrders, equipment, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const priorityOptions = [
    { value: 'all', label: 'All Priorities' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const filteredWorkOrders = workOrders?.filter((wo) => {
    const matchesStatus = statusFilter === 'all' || wo?.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || wo?.priority === priorityFilter;
    return matchesStatus && matchesPriority;
  });

  const getStatusBadge = (status) => {
    const config = {
      pending: { label: 'Pending', color: 'text-warning', bg: 'bg-warning/10', icon: 'Clock' },
      in_progress: { label: 'In Progress', color: 'text-primary', bg: 'bg-primary/10', icon: 'Loader' },
      completed: { label: 'Completed', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' },
      cancelled: { label: 'Cancelled', color: 'text-muted-foreground', bg: 'bg-muted', icon: 'XCircle' }
    };
    const cfg = config?.[status] || config?.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        <Icon name={cfg?.icon} size={12} />
        {cfg?.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { label: 'Low', color: 'text-muted-foreground', bg: 'bg-muted' },
      medium: { label: 'Medium', color: 'text-primary', bg: 'bg-primary/10' },
      high: { label: 'High', color: 'text-warning', bg: 'bg-warning/10' },
      urgent: { label: 'Urgent', color: 'text-error', bg: 'bg-error/10' }
    };
    const cfg = config?.[priority] || config?.medium;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${cfg?.bg} ${cfg?.color}`}>
        {cfg?.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })?.format(amount);
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-xl p-4 border border-border">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              placeholder="Filter by status"
            />
            <Select
              options={priorityOptions}
              value={priorityFilter}
              onChange={setPriorityFilter}
              placeholder="Filter by priority"
            />
          </div>
          <Button variant="primary" iconName="Plus" iconPosition="left">
            Create Work Order
          </Button>
        </div>
      </div>

      {/* Work Orders List */}
      <div className="space-y-4">
        {filteredWorkOrders?.map((wo) => (
          <div key={wo?.id} className="bg-card rounded-xl border border-border hover:shadow-md transition-shadow">
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{wo?.title}</h3>
                    {getPriorityBadge(wo?.priority)}
                    {getStatusBadge(wo?.status)}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{wo?.description}</p>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>
                      <Icon name="Hash" size={14} className="inline mr-1" />
                      {wo?.work_order_number}
                    </span>
                    {wo?.equipment && (
                      <span>
                        <Icon name="Package" size={14} className="inline mr-1" />
                        {wo?.equipment?.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Assigned To</p>
                  <p className="text-sm font-medium text-foreground">
                    {wo?.assigned_to?.full_name || 'Unassigned'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Scheduled Date</p>
                  <p className="text-sm font-medium text-foreground">
                    {wo?.scheduled_date ? new Date(wo?.scheduled_date)?.toLocaleDateString() : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Estimated Cost</p>
                  <p className="text-sm font-medium text-foreground">
                    {wo?.estimated_cost ? formatCurrency(wo?.estimated_cost) : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Actual Cost</p>
                  <p className="text-sm font-medium text-foreground">
                    {wo?.actual_cost ? formatCurrency(wo?.actual_cost) : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <Button variant="outline" size="sm" iconName="Edit" iconPosition="left">
                  Edit
                </Button>
                <Button variant="outline" size="sm" iconName="Eye" iconPosition="left">
                  View Details
                </Button>
                {wo?.status === 'pending' && (
                  <Button variant="primary" size="sm" iconName="Play" iconPosition="left">
                    Start Work
                  </Button>
                )}
                {wo?.status === 'in_progress' && (
                  <Button variant="success" size="sm" iconName="CheckCircle2" iconPosition="left">
                    Complete
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredWorkOrders?.length === 0 && (
        <div className="bg-card rounded-xl p-12 border border-border text-center">
          <Icon name="ClipboardList" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Work Orders Found</h3>
          <p className="text-muted-foreground">Create a new work order to get started</p>
        </div>
      )}
    </div>
  );
};

export default WorkOrdersPanel;